import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { clubsBookingApi } from '@/api/clubs-booking';
import {
  AdminSubscriptionRouteProvider,
  type AdminSubscriptionRouteSnapshot,
} from '@/contexts/AdminSubscriptionRouteContext';
import { auth } from '@/store/authStore';

interface AdminOnlyRouteProps {
  children: React.ReactNode;
}

const AdminOnlyRoute: React.FC<AdminOnlyRouteProps> = ({ children }) => {
  const location = useLocation();
  const user = auth.getUser();
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [routeSubscriptionSnapshot, setRouteSubscriptionSnapshot] = useState<AdminSubscriptionRouteSnapshot | null>(null);

  // Omit `location.pathname` from deps: re-checking on every in-app nav showed the full-screen loader.
  // Access is tied to the signed-in admin; AdminShell still refreshes subscription status on an interval.
  useEffect(() => {
    let active = true;

    const check = async () => {
      if (user.role === 'parent') {
        if (active) {
          setHasAccess(true);
          setCheckingAccess(false);
        }
        return;
      }

      setCheckingAccess(true);
      setRouteSubscriptionSnapshot(null);
      try {
        const access = await clubsBookingApi.getAdminSubscriptionAccessStatus(true, true);
        if (!active) return;
        setHasAccess(access.accessGranted);
        if (access.accessGranted) {
          setRouteSubscriptionSnapshot({ status: access, receivedAtMs: Date.now() });
        } else {
          setRouteSubscriptionSnapshot(null);
        }
      } catch (err: any) {
        if (!active) return;
        const statusCode = Number(err?.response?.status || 0);
        if (statusCode === 402) {
          setHasAccess(false);
        } else {
          // Let the route render when access check request fails for transient network errors.
          setHasAccess(true);
        }
      } finally {
        if (active) {
          setCheckingAccess(false);
        }
      }
    };

    void check();

    return () => {
      active = false;
    };
  }, [user.role, user.userId]);

  if (user.role === 'parent') {
    return <Navigate to="/my-bookings" replace />;
  }

  if (checkingAccess) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f1f2f7] p-6">
        <div
          className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="relative mx-auto mb-6 flex h-16 w-16 items-center justify-center">
            <span
              className="absolute inline-flex h-full w-full rounded-full bg-emerald-400/20 opacity-75 animate-ping"
              aria-hidden
            />
            <span
              className="relative inline-flex h-12 w-12 rounded-full border-[3px] border-emerald-100 border-t-emerald-600 animate-spin"
              style={{ animationDuration: '0.85s' }}
              aria-hidden
            />
          </div>
          <p className="text-sm font-medium text-slate-800">Loading your account</p>
        </div>
      </main>
    );
  }

  if (!hasAccess) {
    return <Navigate to="/billing-required" replace state={{ from: location }} />;
  }

  return (
    <AdminSubscriptionRouteProvider snapshot={routeSubscriptionSnapshot}>
      {children}
    </AdminSubscriptionRouteProvider>
  );
};

export default AdminOnlyRoute;
